import { useState } from "react";
import { Button, Overlay, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ShoppingCart } from "lucide-react";
import Price from "../../components/Price";
import localizedLink from "../../utils/localizedLink";
import productTitle from "../../utils/productTitle";
import useLanguage from "../../hooks/useLanguage";

// Compatible accessories, listed by id in a product's `upsell` field. Kept
// compact (small thumbnails, no heading) so it reads as part of the buy box.
export default function Upsell({ products, addToCart }) {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [showStates, setShowStates] = useState({});
  const [targetStates, setTargetStates] = useState({});

  if (!products.length) return null;

  const handleClick = (event, product) => {
    addToCart(product);
    const target = event.currentTarget;
    setShowStates((prev) => ({ ...prev, [product.id]: true }));
    setTargetStates((prev) => ({ ...prev, [product.id]: target }));

    setTimeout(() => {
      setShowStates((prev) => ({ ...prev, [product.id]: false }));
    }, 2000);
  };

  return (
    // border-[1px], not `border`: Bootstrap's `.border` is !important and
    // would force its own gray color.
    <ul className="mt-4 divide-y divide-[#eaeaec] rounded-2xl border-[1px] border-[#080a0f] px-4">
      {products.map((product) => (
        <li key={product.id} className="flex items-center gap-3 py-3">
          {/* Same link as the title, so it's hidden from keyboard and screen
              readers to avoid announcing it twice. */}
          <Link
            to={localizedLink(`/${product.id}`, language)}
            tabIndex={-1}
            aria-hidden="true"
            className="shrink-0"
          >
            {/* Transparent product images sit on gray, as on the cards. */}
            <img
              src={product.imageUrl}
              loading="lazy"
              alt=""
              className="h-16 w-16 rounded-xl bg-[#eaeaec] object-contain p-1.5"
            />
          </Link>
          <div className="min-w-0 flex-1">
            <Link
              to={localizedLink(`/${product.id}`, language)}
              className="font-medium hover:underline"
            >
              {productTitle(product, language)}
            </Link>
            <Price product={product} size="sm" className="mt-1" />
          </div>
          <Button
            variant="dark"
            aria-label={t("product.add-to-cart")}
            className="eufy-button inline-flex shrink-0 items-center"
            onClick={(e) => handleClick(e, product)}
          >
            <ShoppingCart className="h-[18px] w-[18px]" strokeWidth={2} />
          </Button>
          <Overlay
            show={showStates[product.id] || false}
            target={targetStates[product.id]}
            placement="top"
          >
            <Tooltip id={`upsell-tooltip-${product.id}`}>
              {t("product.product-added-to-cart")}
            </Tooltip>
          </Overlay>
        </li>
      ))}
    </ul>
  );
}
